/**
 * The auth context shape and hook, kept separate from the provider component so
 * consumers import the hook without pulling the whole Cognito wiring, and so the
 * provider file only exports a component.
 */
import { createContext, useContext } from 'react';

/** Where the user is in the auth lifecycle. `loading` = restoring a session. */
export type AuthStatus = 'loading' | 'signedIn' | 'signedOut';

/** The minimal identity the UI needs. */
export interface AuthUser {
  readonly email: string;
}

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** The current Cognito ID token, or null when signed out. May be stale; call {@link getIdToken} for a fresh one before an API request. */
  readonly idToken: string | null;
  /** Register a new user. Resolves once Cognito accepts the sign-up (a confirm code is emailed). */
  readonly signUp: (email: string, password: string) => Promise<void>;
  /** Confirm a sign-up with the emailed code. */
  readonly confirm: (email: string, code: string) => Promise<void>;
  /** Resend the confirmation code to an unconfirmed user. Rejects if the account is already confirmed. */
  readonly resendCode: (email: string) => Promise<void>;
  /** Sign in with SRP. On success the session is persisted and state flips to `signedIn`. */
  readonly signIn: (email: string, password: string) => Promise<void>;
  /** Start a password reset. Cognito emails a reset code to the address if an account exists. */
  readonly forgotPassword: (email: string) => Promise<void>;
  /** Complete a password reset with the emailed code and a new password. */
  readonly confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Clear the persisted session and local state. */
  readonly signOut: () => void;
  /** Resolve a fresh, valid Cognito ID token, refreshing it if it has expired. Rejects when signed out. */
  readonly getIdToken: () => Promise<string>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** Access the auth context. Throws if used outside an {@link AuthProvider}. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return value;
}
